/**
 * What can this printing actually be?
 *
 * A card in hand differs from its catalogue entry along four independent axes,
 * and they do not come from the same place:
 *
 *   type     normal | holo | reverse        property of the printing
 *   foil     pokeball, masterball, cosmos…  property of the printing
 *   subtype  shadowless, unlimited, …       only the owner can say
 *   stamps   1st-edition, staff, …          only the owner can say, and it is a list
 *
 * The first two are knowable, so the interface should not ask about them when
 * only one answer exists. The last two are not, so it must ask.
 *
 * A stamp sits on top of a subtype rather than replacing it, which is why Base
 * Set Charizard has a printing called "Holo / Shadowless / 1st Edition Stamp".
 * Modelling these as one field is what makes that card impossible to describe.
 *
 * Source is digitalcards.card_variants. TCGdex carries five booleans and knows
 * nothing about shadowless, print runs or stamps, so it cannot answer this.
 */

import { supabase } from '../../lib/supabase';
import { labelFor, variantLabel } from './variantLabel';
import { reduceVariants } from './reduceVariants';

const SCHEMA = 'digitalcards';

/**
 * Reduce every recorded printing of one card into the choices worth offering.
 *
 * Rows that carry no axis at all are ignored rather than counted. The catalogue
 * holds ~1,800 plain rows attached to the wrong card by an earlier ingest; they
 * contribute only an image, so letting them vote on `type` would put a Normal
 * option on cards that never had one.
 *
 * @returns {Promise<{types, foilsByType, subtypes, stamps, count}>}
 */
export async function getVariantOptions(cardId) {
  const empty = { types: [], foilsByType: {}, subtypes: [], stamps: [], count: 0 };
  if (!cardId) return empty;

  const { data, error } = await supabase
    .schema(SCHEMA)
    .from('card_variants')
    .select('type, subtype, stamps, foil')
    .eq('card_id', cardId);

  if (error) {
    console.warn('[VariantOptions] read failed:', error.message);
    return empty;
  }
  return reduceVariants(data);
}

export { reduceVariants };
export { labelFor, variantLabel };
export default { getVariantOptions, reduceVariants, variantLabel, labelFor };
